import { createContext, useCallback, useContext, useEffect, useRef, useState } from "react";
import PropTypes from "prop-types";
import { replaceAuthHeader } from "services/authHeader";
import { hasAuthState, subscribeToTokenChanges } from "services/tokenStorage";
import {
  effectiveRefreshSkewMs,
  refreshAccessToken,
  refreshIfExpiring,
} from "services/tokenRefresh";
import { endSession } from "utils/logoutHelper";

const SessionContext = createContext({ sessionExpired: false, dismissSessionExpired: () => {} });

// URL fragments that should NOT trigger a refresh-and-retry or the
// session-expired popup. The login endpoint legitimately returns 401 for bad
// credentials, /refresh reports its own outcome to the caller, and a logout
// should never renew the very token it is discarding.
const SESSION_EXEMPT_URL_FRAGMENTS = ["/api/auth/login", "/api/auth/refresh", "/api/auth/logout"];

// Inactivity timeout: log the user out after 1h with no user interaction.
const IDLE_LIMIT_MS = 60 * 60 * 1000; // 1 hour
// While the user is active, check whether the token needs sliding at most this
// often. Never longer than the refresh lead time itself, or a short-lived token
// could expire between two checks.
const MAX_REFRESH_CHECK_INTERVAL_MS = 60 * 1000; // 1 minute
const refreshCheckInterval = () =>
  Math.max(5000, Math.min(MAX_REFRESH_CHECK_INTERVAL_MS, effectiveRefreshSkewMs()));
// User-input events that count as "activity" and keep the session alive.
const ACTIVITY_EVENTS = [
  "mousemove",
  "mousedown",
  "keydown",
  "scroll",
  "touchstart",
  "click",
  "wheel",
];
// Last user activity in ANY tab, so working in one tab keeps the others alive.
// Written at most this often to keep storage traffic down.
const SHARED_ACTIVITY_KEY = "session_last_activity";
const SHARED_ACTIVITY_WRITE_MS = 5000;
// Storage keys whose change in another tab means the login itself changed.
const AUTH_STORAGE_KEYS = ["user", "user_id", "auth_token", "refresh_token"];
const SIGN_IN_PATH = "/authentication/sign-in";

function readStorage(key) {
  try {
    return localStorage.getItem(key);
  } catch (e) {
    return null;
  }
}

function writeSharedActivity(time) {
  try {
    localStorage.setItem(SHARED_ACTIVITY_KEY, String(time));
  } catch (e) {
    // storage disabled — idle tracking falls back to this tab only
  }
}

const readSharedActivity = () => Number(readStorage(SHARED_ACTIVITY_KEY)) || 0;
const onAuthPage = () => window.location.pathname.startsWith("/authentication");

function requestUrl(input) {
  if (typeof input === "string") return input;
  if (typeof Request !== "undefined" && input instanceof Request) return input.url;
  if (typeof URL !== "undefined" && input instanceof URL) return input.toString();
  if (input && typeof input === "object" && "url" in input) return String(input.url);
  return "";
}

function isSessionExemptRequest(input) {
  const urlString = requestUrl(input);
  return SESSION_EXEMPT_URL_FRAGMENTS.some((frag) => urlString.includes(frag));
}

export function SessionProvider({ children }) {
  const [sessionExpired, setSessionExpired] = useState(false);

  /* Show the dialog AND end the session right away, so the tokens stop working
     while the dialog waits for a click — otherwise a new tab would walk straight
     back in. Other tabs see the cleared storage and go to the sign-in page. */
  const expireSession = useCallback(() => {
    setSessionExpired(true);
    endSession();
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || window.__fetchSessionWrapped) return;
    window.__fetchSessionWrapped = true;

    const originalFetch = window.fetch.bind(window);

    /* A 401 means the access token is gone or expired. Renew it once and replay
       the request, so an expiring token is invisible to the caller. Only when
       the refresh itself is rejected is the session actually over. */
    window.fetch = async (input, init) => {
      if (isSessionExemptRequest(input)) {
        return originalFetch(input, init);
      }

      // Requests can only be replayed if their body is still readable, so take
      // a copy of a Request-shaped input before the first attempt consumes it.
      const isRequestObject = typeof Request !== "undefined" && input instanceof Request;
      const replayable = isRequestObject ? input.clone() : null;

      const response = await originalFetch(input, init);

      if (response.status !== 401 || !hasAuthState()) {
        return response;
      }

      let result;
      try {
        result = await refreshAccessToken();
      } catch (e) {
        return response;
      }

      if (!result.ok) {
        // Rejected refresh token → the user has to log in again. A transient
        // failure (offline, 5xx) leaves the session alone and just surfaces
        // the original 401 to the caller.
        if (result.expired) expireSession();
        return response;
      }

      try {
        const retried = isRequestObject
          ? await originalFetch(
              new Request(replayable, {
                headers: replaceAuthHeader(replayable.headers, result.token),
              })
            )
          : await originalFetch(input, {
              ...init,
              headers: replaceAuthHeader(init && init.headers, result.token),
            });

        // Still refused with a freshly minted token — treat as a dead session
        // rather than silently swallowing it.
        if (retried.status === 401) expireSession();
        return retried;
      } catch (e) {
        // The replay failed (unreadable body, network drop). Hand back the
        // original response; the token has still been renewed for next time.
        return response;
      }
    };
  }, [expireSession]);

  // ---- Inactivity (idle) timeout + sliding token refresh ----
  const lastActivityRef = useRef(Date.now());
  const sessionExpiredRef = useRef(sessionExpired);
  sessionExpiredRef.current = sessionExpired;

  /* Signing in is a client-side navigate(), so this provider is never
     remounted. Track the auth state in React state and let the watcher below
     depend on it, otherwise it would only ever arm on a full page load — i.e.
     never for a user who just logged in. */
  const [authed, setAuthed] = useState(() => hasAuthState());

  useEffect(() => {
    let knownUserId = readStorage("user_id");

    /* Bring this tab in line with the login currently in storage:
         - logged out elsewhere (Logout, expiry, another tab) → sign-in page
         - a different user signed in elsewhere → reload as that user */
    const sync = () => {
      const signedIn = hasAuthState();
      const userId = readStorage("user_id");
      setAuthed(signedIn);

      if (!signedIn) {
        if (knownUserId && !onAuthPage()) window.location.replace(SIGN_IN_PATH);
      } else if (knownUserId && userId && userId !== knownUserId) {
        window.location.replace("/dashboard");
      }

      knownUserId = signedIn ? userId : null;
    };

    // `storage` fires for changes made by *other* tabs. A null key means
    // localStorage.clear(), which is how logout wipes the session.
    const onStorage = (event) => {
      if (event.key === null || AUTH_STORAGE_KEYS.includes(event.key)) sync();
    };

    // A page restored from the back/forward cache missed every storage event
    // while it was frozen, e.g. Back after logging out.
    const onPageShow = (event) => {
      if (event.persisted) sync();
    };

    const unsubscribe = subscribeToTokenChanges(sync);
    window.addEventListener("storage", onStorage);
    window.addEventListener("pageshow", onPageShow);
    return () => {
      unsubscribe();
      window.removeEventListener("storage", onStorage);
      window.removeEventListener("pageshow", onPageShow);
    };
  }, []);

  useEffect(() => {
    if (typeof window === "undefined" || !authed) return undefined;

    lastActivityRef.current = Date.now();
    let lastSharedWrite = lastActivityRef.current;
    writeSharedActivity(lastSharedWrite);

    const onActivity = () => {
      const now = Date.now();
      lastActivityRef.current = now;
      if (now - lastSharedWrite >= SHARED_ACTIVITY_WRITE_MS) {
        lastSharedWrite = now;
        writeSharedActivity(now);
      }
    };

    /* One timer drives both concerns, so a user who is reading rather than
       clicking still gets their token renewed:
         - idle past the limit  → expire the session
         - token near expiry    → renew it now, before a request can fail */
    let timer = null;

    // Each branch that ends the session returns without rescheduling; the
    // effect re-arms if `authed` changes.
    const tick = async () => {
      if (sessionExpiredRef.current) return;

      if (!hasAuthState()) {
        setAuthed(false);
        return;
      }

      const lastActivity = Math.max(lastActivityRef.current, readSharedActivity());
      if (Date.now() - lastActivity >= IDLE_LIMIT_MS) {
        expireSession();
        return;
      }

      const result = await refreshIfExpiring();
      if (!result.ok && result.expired) {
        expireSession();
        return;
      }

      timer = setTimeout(tick, refreshCheckInterval());
    };

    timer = setTimeout(tick, refreshCheckInterval());
    ACTIVITY_EVENTS.forEach((evt) => window.addEventListener(evt, onActivity, { passive: true }));

    return () => {
      ACTIVITY_EVENTS.forEach((evt) => window.removeEventListener(evt, onActivity));
      if (timer) clearTimeout(timer);
    };
  }, [authed, expireSession]);

  const dismissSessionExpired = () => setSessionExpired(false);

  return (
    <SessionContext.Provider value={{ sessionExpired, dismissSessionExpired }}>
      {children}
    </SessionContext.Provider>
  );
}

SessionProvider.propTypes = {
  children: PropTypes.node.isRequired,
};

export function useSession() {
  return useContext(SessionContext);
}
